import React, { useEffect, useMemo, useState, useReducer } from "react";
import { useTranslation } from "react-i18next";
import ProductGameHeader from "../ProductGameHeader";
import UserSession from "network/UserSession";
import { QuizSocketService } from "./QuizSocketService";
import { QuizMode, RoundState } from "./QuizProtocol";
import { InGameViewProps } from "views/GamePage";
import Chatbox from "components/Chatbox";
import UserListModal from "components/common/UserListModal";
import ChatFloatButton from "components/common/ChatFloatButton";
import ChatModal from "components/common/ChatModal";
import RuleModal from "components/common/RuleModal";
import quizReducer, { initialState } from "./QuizReducer";
import "sass/views/quiz/QuizView.scss";

const QuizView: React.FC<InGameViewProps> = ({
  product,
  room,
  messages,
  setMessages,
  onReturnToWaitingRoom,
}) => {
  const { t } = useTranslation();

  const myId = UserSession.getInstance().getUserId();

  const [quizState, dispatch] = useReducer(quizReducer, initialState);
  const {
    mode,
    question,
    roundState,
    responses,
    showResults,
    hasSubmitted,
    players,
    oCount,
    xCount,
  } = quizState;

  // UI State
  const [inputQuestion, setInputQuestion] = useState("");
  const [answerInput, setAnswerInput] = useState("");
  const [isRuleOpen, setIsRuleOpen] = useState(false);
  const [isSettingOpen, setIsSettingOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isUserListOpen, setIsUserListOpen] = useState(false);

  // Optimize player lookup with useMemo
  const myPlayer = useMemo(
    () => players.find((p) => p.id === myId),
    [players, myId]
  );

  // Host detection
  const isHost = myPlayer?.isHost || false;

  const socketService = useMemo(
    () => new QuizSocketService(dispatch),
    [dispatch]
  );

  useEffect(() => {
    socketService.initializeListeners();
    return () => {
      socketService.removeListeners();
    };
  }, [socketService]);

  // Host Control Handlers
  const handleModeChange = (newMode: QuizMode) => {
    if (!isHost || roundState === RoundState.ACTIVE) return;
    console.log("Mode changing from", mode, "to", newMode);
    // Optimistic update - 즉시 클라이언트 상태 변경
    dispatch({ type: "SET_MODE", payload: newMode });
    // 서버에도 요청 전송
    socketService.setMode(newMode);
    // 모드 변경 시 질문 입력 칸 초기화
    setInputQuestion("");
  };

  const handleStartRound = () => {
    if (!isHost || roundState === RoundState.ACTIVE) return;
    const questionToUse =
      mode === QuizMode.BUZZER ? inputQuestion || "" : inputQuestion;
    socketService.startRound(questionToUse);
    // 질문 입력 칸을 비우지 않음 (초기화 버튼을 누를 때까지 유지)
  };

  const handleStopRound = () => {
    if (!isHost || roundState !== RoundState.ACTIVE) return;
    socketService.stopRound();
  };

  const handleContinueRound = () => {
    if (!isHost || roundState !== RoundState.STOPPED) return;
    // 현재 입력된 질문으로 업데이트하여 이어하기
    const questionToUse = inputQuestion || question;
    socketService.continueRound(questionToUse);
  };

  const handleResetRound = () => {
    if (!isHost || roundState !== RoundState.STOPPED) return;
    socketService.resetRound();
    dispatch({ type: "RESET_LOCAL_STATE" });
    setInputQuestion(""); // 질문 입력 필드도 초기화
  };

  const handleRevealResponses = () => {
    if (!isHost || roundState !== RoundState.STOPPED) return;
    socketService.revealResponses();
  };

  // Player Action Handlers
  const handleBuzzerPress = () => {
    if (
      hasSubmitted ||
      roundState !== RoundState.ACTIVE ||
      mode !== QuizMode.BUZZER
    )
      return;
    socketService.pressBuzzer();
  };

  const handleOXSubmit = (choice: "O" | "X") => {
    if (
      hasSubmitted ||
      roundState !== RoundState.ACTIVE ||
      mode !== QuizMode.OX
    )
      return;
    socketService.submitOX(choice);
  };

  const handleAnswerSubmit = () => {
    if (
      hasSubmitted ||
      roundState !== RoundState.ACTIVE ||
      mode !== QuizMode.ANSWER_INPUT ||
      !answerInput.trim()
    )
      return;
    socketService.submitAnswer(answerInput.trim());
    setAnswerInput("");
  };

  // Render Functions - Optimized UI from previous implementation
  const renderModeButtons = () => {
    if (!isHost) return null;

    return (
      <div className="flex justify-center mb-4">
        <div className="grid grid-cols-3 gap-1 bg-black/40 backdrop-blur-sm p-1 rounded-lg">
          <button
            onClick={() => handleModeChange(QuizMode.BUZZER)}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              mode === QuizMode.BUZZER
                ? "bg-red-600 text-white"
                : "text-gray-400 hover:text-white"
            }`}
            disabled={roundState === RoundState.ACTIVE}
          >
            <span className="mr-2">🔔</span>
            {t("Quiz.buzzerMode")}
          </button>
          <button
            onClick={() => handleModeChange(QuizMode.OX)}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              mode === QuizMode.OX
                ? "bg-yellow-600 text-white"
                : "text-gray-400 hover:text-white"
            }`}
            disabled={roundState === RoundState.ACTIVE}
          >
            <span className="mr-2">⭕</span>
            {t("Quiz.oxMode")}
          </button>
          <button
            onClick={() => handleModeChange(QuizMode.ANSWER_INPUT)}
            className={`px-4 py-2 rounded-md font-medium transition-colors ${
              mode === QuizMode.ANSWER_INPUT
                ? "bg-indigo-600 text-white"
                : "text-gray-400 hover:text-white"
            }`}
            disabled={roundState === RoundState.ACTIVE}
          >
            <span className="mr-2">✏️</span>
            {t("Quiz.answerMode")}
          </button>
        </div>
      </div>
    );
  };

  // Question display - always visible with fixed height
  const renderQuestionArea = () => {
    return (
      <div className="bg-gradient-to-r from-blue-900/90 via-teal-800/70 to-cyan-900/90 backdrop-blur-sm rounded-lg p-4 text-center flex items-center justify-center min-h-[100px] border border-teal-600/40">
        {question && roundState !== RoundState.WAITING ? (
          <h2
            className="text-2xl md:text-3xl font-bold text-white break-words leading-relaxed overflow-hidden"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: 4,
              WebkitBoxOrient: "vertical",
              maxHeight: "4.8em",
            }}
          >
            {question}
          </h2>
        ) : (
          <div className="text-gray-300/50 text-lg">
            {isHost ? "" : t("Quiz.playerWaitingQuestion")}
          </div>
        )}
      </div>
    );
  };

  const renderHostControls = () => {
    if (!isHost) return null;

    return (
      <div className="space-y-3 mb-4">
        <div>
          <textarea
            value={inputQuestion}
            onChange={(e) => setInputQuestion(e.target.value)}
            placeholder={t("Quiz.enterQuestion")}
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none transition-colors resize-none"
            disabled={roundState === RoundState.ACTIVE}
            maxLength={40}
            rows={2}
            style={{ lineHeight: "1.5" }}
          />
          <div className="text-xs text-gray-500 mt-1 text-right">
            {inputQuestion.length}/40
          </div>
        </div>
        <div className="flex flex-row gap-2 justify-center">
          {roundState === RoundState.WAITING && (
            <button
              className="px-3 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-bold text-base w-24"
              onClick={handleStartRound}
            >
              {t("Quiz.start")}
            </button>
          )}
          {roundState === RoundState.ACTIVE && (
            <button
              className="px-3 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-bold text-base w-24"
              onClick={handleStopRound}
            >
              {t("Quiz.stop")}
            </button>
          )}
          {roundState === RoundState.STOPPED && (
            <>
              <button
                className="py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-bold text-base w-24"
                onClick={handleContinueRound}
              >
                {t("Quiz.continue")}
              </button>
              <button
                className="py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-bold text-base w-24"
                onClick={handleRevealResponses}
              >
                {t("Quiz.revealResponses")}
              </button>
              <button
                className="py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors font-bold text-base w-24"
                onClick={handleResetRound}
              >
                {t("Quiz.reset")}
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderPlayerControls = () => {
    if (isHost) return null;

    if (hasSubmitted) {
      return (
        <div className="text-center py-8">
          <div className="bg-green-800/50 rounded-lg border border-green-600 p-6">
            <div className="text-4xl text-green-400 mb-4">✅</div>
            <p className="text-lg text-green-300">{t("Quiz.submitted")}</p>
            <p className="text-sm text-green-400 mt-2">
              {t("Quiz.waitingForOthers")}
            </p>
          </div>
        </div>
      );
    }

    // Mode-specific controls - clean interface with mode title and interaction UI
    switch (mode) {
      case QuizMode.BUZZER:
        return (
          <div className="text-center py-6">
            <div className="space-y-4">
              <div className="mb-4">
                <h3 className="text-base font-medium text-gray-300 mb-2">
                  {t("Quiz.buzzerMode")}
                </h3>
              </div>
              <button
                className={`w-40 h-40 text-white rounded-full transform transition-all ${
                  roundState === RoundState.ACTIVE
                    ? "bg-gradient-to-b from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 active:shadow-md border-2 border-green-300"
                    : "bg-gray-600 cursor-not-allowed opacity-60 shadow-md"
                }`}
                onClick={handleBuzzerPress}
                disabled={roundState !== RoundState.ACTIVE}
                style={{
                  boxShadow:
                    roundState === RoundState.ACTIVE
                      ? "0 8px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                      : undefined,
                }}
              >
                <div className="text-4xl">🔔</div>
                {/* <div className="text-sm font-bold">{t("Quiz.buzzer")}</div> */}
              </button>
            </div>
          </div>
        );

      case QuizMode.OX:
        return (
          <div className="py-6">
            <div className="text-center mb-4">
              <h3 className="text-base font-medium text-gray-300 mb-2">
                {t("Quiz.oxMode")}
              </h3>
            </div>
            <div className="flex justify-center gap-4">
              <button
                className={`w-20 h-20 text-white rounded-full text-2xl font-bold shadow-lg transform transition-all ${
                  roundState === RoundState.ACTIVE
                    ? "bg-green-600 hover:bg-green-700 hover:scale-105"
                    : "bg-gray-600 cursor-not-allowed opacity-60"
                }`}
                onClick={() => handleOXSubmit("O")}
                disabled={roundState !== RoundState.ACTIVE}
              >
                O
              </button>
              <button
                className={`w-20 h-20 text-white rounded-full text-2xl font-bold shadow-lg transform transition-all ${
                  roundState === RoundState.ACTIVE
                    ? "bg-red-600 hover:bg-red-700 hover:scale-105"
                    : "bg-gray-600 cursor-not-allowed opacity-60"
                }`}
                onClick={() => handleOXSubmit("X")}
                disabled={roundState !== RoundState.ACTIVE}
              >
                X
              </button>
            </div>
          </div>
        );

      case QuizMode.ANSWER_INPUT:
        return (
          <div className="space-y-3 py-4">
            <div className="text-center mb-3">
              <h3 className="text-base font-medium text-gray-300 mb-2">
                {t("Quiz.answerMode")}
              </h3>
            </div>
            <input
              type="text"
              value={answerInput}
              onChange={(e) => setAnswerInput(e.target.value)}
              placeholder={t("Quiz.enterAnswer")}
              className={`w-full px-4 py-3 border rounded-lg text-white placeholder-gray-400 focus:outline-none transition-colors ${
                roundState === RoundState.ACTIVE
                  ? "bg-gray-700 border-gray-600 focus:border-blue-500"
                  : "bg-gray-800 border-gray-700 cursor-not-allowed opacity-60"
              }`}
              onKeyDown={(e) => {
                if (e.key === "Enter" && roundState === RoundState.ACTIVE) {
                  handleAnswerSubmit();
                }
              }}
              disabled={roundState !== RoundState.ACTIVE}
            />
            <button
              className={`w-full px-4 py-2 rounded-lg transition-colors font-medium text-sm ${
                answerInput.trim() && roundState === RoundState.ACTIVE
                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                  : "bg-gray-600 text-gray-400 cursor-not-allowed"
              }`}
              onClick={handleAnswerSubmit}
              disabled={!answerInput.trim() || roundState !== RoundState.ACTIVE}
            >
              <span className="mr-2">✈️</span>
              {t("Quiz.submit")}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  // Optimized responses display - combining live and results into one function
  const renderResponses = () => {
    if (responses.length === 0) return null;

    return (
      <div className="space-y-1">
        <div className="text-xs text-gray-500 mb-2">
          {mode === QuizMode.BUZZER && t("Quiz.buzzerOrder")}
          {mode === QuizMode.OX &&
            showResults &&
            `O: ${oCount || 0}, X: ${xCount || 0}`}
        </div>
        {responses
          .sort((a, b) => {
            if (mode === QuizMode.BUZZER && a.order && b.order) {
              return a.order - b.order;
            }
            return a.timestamp - b.timestamp;
          })
          .map((response, index) => {
            // Check if this response is from a bot (hidden AI)
            const isBot =
              players.find((p) => p.id === response.playerId)?.isBot || false;

            return (
              <div
                key={response.playerId}
                className={`flex items-center gap-2 p-2 rounded text-xs ${
                  isBot ? "bg-gray-800/10 opacity-40" : "bg-gray-800/30"
                }`}
              >
                <div
                  className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs ${
                    isBot
                      ? "bg-gray-600 text-gray-400"
                      : "bg-blue-600 text-white"
                  }`}
                >
                  {mode === QuizMode.BUZZER
                    ? response.order || index + 1
                    : index + 1}
                </div>
                <div
                  className={`flex-1 font-medium text-xs ${
                    isBot ? "text-gray-500" : "text-white"
                  }`}
                >
                  {response.playerName}
                </div>
                {showResults && mode === QuizMode.OX && (
                  <div
                    className={`px-2 py-1 rounded font-bold text-xs ${
                      isBot
                        ? "bg-gray-700/20 text-gray-500"
                        : response.choice === "O"
                        ? "bg-green-600/20 text-green-300"
                        : "bg-red-600/20 text-red-300"
                    }`}
                  >
                    {response.choice}
                  </div>
                )}
                {showResults && mode === QuizMode.ANSWER_INPUT && (
                  <div
                    className={`px-2 py-1 rounded text-xs max-w-32 truncate ${
                      isBot
                        ? "bg-gray-700/20 text-gray-500"
                        : "bg-purple-600/20 text-purple-300"
                    }`}
                  >
                    {response.answer}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    );
  };

  // 채팅 모달
  const renderChatModal = () => (
    <ChatModal isOpen={isChatOpen} onClose={() => setIsChatOpen(false)}>
      <Chatbox
        product={product}
        roomName={room.roomName}
        userList={players.map((player) => ({
          socketId: `quiz-${player.id}`,
          defaultNickName: player.name,
          nickName: player.name,
          id: player.id,
          isHost: player.isHost,
          state: "ingame" as any,
          isAI: player.isBot || false,
        }))}
        chatList={messages}
        setChatList={setMessages}
        smallChatKey="quiz"
        isUserListOpen={false}
        setIsUserListOpen={() => {}}
      />
    </ChatModal>
  );

  return (
    <div className="space-gray-theme quiz-container flex flex-col h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <ProductGameHeader
        product={product}
        room={room}
        isRuleOpen={isRuleOpen}
        setIsRuleOpen={setIsRuleOpen}
        isUserListOpen={isUserListOpen}
        setIsUserListOpen={setIsUserListOpen}
        isSettingOpen={isSettingOpen}
        setIsSettingOpen={setIsSettingOpen}
        userCount={players.length}
        t={t}
      >
        {/* Leave Room button */}
        <button
          className="w-full px-4 py-2.5 bg-gray-800 hover:bg-red-600 text-red-400 hover:text-white transition-colors font-semibold flex items-center justify-center "
          onClick={() => onReturnToWaitingRoom()}
        >
          <i className="fas fa-sign-out-alt mr-2" />
          {t("leaveRoom")}
        </button>
      </ProductGameHeader>

      {isRuleOpen && (
        <RuleModal
          isOpen={isRuleOpen}
          recreationId={product.id}
          inGame={true}
        />
      )}

      <div className="flex flex-col flex-1 mt-[56px] pb-40">
        <div className="max-w-4xl mx-auto px-4 py-2 w-full space-y-4">
          {/* Header */}
          {renderModeButtons()}
          {renderQuestionArea()}

          {/* Game Controls */}
          {isHost ? renderHostControls() : renderPlayerControls()}
        </div>
      </div>

      {/* Fixed Bottom Responses Section */}
      <div className="fixed bottom-0 left-0 right-0 h-40 bg-black/80 backdrop-blur-sm border-t border-gray-600">
        <div className="max-w-4xl mx-auto h-full flex flex-col">
          <div className="px-4 py-2 border-b border-gray-700 flex-shrink-0">
            <h3 className="text-sm font-medium text-gray-300">
              {t("Quiz.responses")}
            </h3>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-2">
            {responses.length > 0 ? (
              renderResponses()
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                <p className="text-xs">
                  {isHost
                    ? t("Quiz.waitingForResponses")
                    : t("Quiz.noResponsesYet")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <ChatFloatButton
        onClick={() => setIsChatOpen(!isChatOpen)}
        isOpen={isChatOpen}
      />

      {renderChatModal()}

      {/* Standalone UserList Modal */}
      <UserListModal
        userList={players.map((player) => ({
          socketId: `quiz-${player.id}`,
          defaultNickName: player.name,
          nickName: player.name,
          id: player.id,
          isHost: player.isHost,
          state: "ingame",
          isAI: player.isBot || false,
        }))}
        isOpen={isUserListOpen}
        onClose={() => setIsUserListOpen(false)}
        variant="standalone"
      />
    </div>
  );
};

export default QuizView;
